import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixFormsAiV1ChatSettings from './forms-ai-v1-chat-settings-chat-settings.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface ChatSettings {
  /**
   * The ID of the chat settings. The chat settings specified in this object apply to the form with a matching ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Custom greeting message displayed when a visitor opens the AI chat.
   * @maxLength 50
   */
  greetingMessage?: string | null;
  /**
   * Whether AI chat is enabled for this form.
   *
   * Default: `true`
   */
  chatEnabled?: boolean | null;
  /**
   * Revision number, which increments by 1 each time the chat settings are updated.
   * To prevent conflicting changes, the current revision must be passed when updating the chat settings.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the chat settings were created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the chat settings were last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Message that summarizes the contents of the form. Appears after the greeting message.
   * @maxLength 255
   */
  summaryMessage?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /**
   * Whether the summary message is manually set or AI-generated.
   * When `false` the summary message is automatically generated by AI when the chat settings are saved.
   * When `true`, the summary message can be manually set and won't be overwritten by AI.
   *
   * Default: `false`
   */
  manualSummary?: boolean | null;
  /**
   * Personality of the AI when interacting with visitors.
   * Defines the communication style of the AI chat responses.
   */
  personality?: PersonalityWithLiterals;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export enum Personality {
  PROFESSIONAL = 'PROFESSIONAL',
  FRIENDLY = 'FRIENDLY',
  PLAYFUL = 'PLAYFUL',
  DIRECT = 'DIRECT',
}

/** @enumType */
export type PersonalityWithLiterals =
  | Personality
  | 'PROFESSIONAL'
  | 'FRIENDLY'
  | 'PLAYFUL'
  | 'DIRECT';

export interface CreateChatSettingsRequest {
  /** Chat settings to be created. */
  chatSettings: ChatSettings;
}

export interface CreateChatSettingsResponse {
  /** Created chat settings. */
  chatSettings?: ChatSettings;
}

export interface UpdateChatSettingsRequest {
  /** Chat settings to be updated. */
  chatSettings: ChatSettings;
}

export interface UpdateChatSettingsResponse {
  /** Updated chat settings. */
  chatSettings?: ChatSettings;
}

export interface DeleteChatSettingsRequest {
  /**
   * Chat settings ID.
   * @format GUID
   */
  chatSettingsId: string;
}

export interface DeleteChatSettingsResponse {}

export interface GetChatSettingsRequest {
  /**
   * Chat settings ID.
   * @format GUID
   */
  chatSettingsId: string;
}

export interface GetChatSettingsResponse {
  /** Retrieved chat settings. */
  chatSettings?: ChatSettings;
}

export interface QueryChatSettingsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryChatSettingsResponse {
  /** List of chat settings matching the query. */
  chatSettings?: ChatSettings[];
  /** Metadata about the pagination of the results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ChatSettingsCreatedEnvelope {
  entity: ChatSettings;
  metadata: EventMetadata;
}

/** @permissionScope Manage Challenges
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionScope Manage Intake Form
 * @permissionScopeId SCOPE.INTAKE-FORM.MANAGE
 * @permissionScope Manage Intake Form (PII)
 * @permissionScopeId SCOPE.INTAKE-FORM.MANAGE_LIMITED
 * @permissionId WIX_FORMS.FORM_SCHEMA_CREATE
 * @webhook
 * @eventType wix.forms.ai.v1.chat_settings_created
 * @slug created
 */
export declare function onChatSettingsCreated(
  handler: (event: ChatSettingsCreatedEnvelope) => void | Promise<void>
): void;

export interface ChatSettingsDeletedEnvelope {
  metadata: EventMetadata;
}

/** @permissionScope Manage Challenges
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionScope Manage Intake Form
 * @permissionScopeId SCOPE.INTAKE-FORM.MANAGE
 * @permissionScope Manage Intake Form (PII)
 * @permissionScopeId SCOPE.INTAKE-FORM.MANAGE_LIMITED
 * @permissionId WIX_FORMS.FORM_SCHEMA_CREATE
 * @webhook
 * @eventType wix.forms.ai.v1.chat_settings_deleted
 * @slug deleted
 */
export declare function onChatSettingsDeleted(
  handler: (event: ChatSettingsDeletedEnvelope) => void | Promise<void>
): void;

export interface ChatSettingsUpdatedEnvelope {
  entity: ChatSettings;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/** @permissionScope Manage Challenges
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionScope Manage Intake Form
 * @permissionScopeId SCOPE.INTAKE-FORM.MANAGE
 * @permissionScope Manage Intake Form (PII)
 * @permissionScopeId SCOPE.INTAKE-FORM.MANAGE_LIMITED
 * @permissionId WIX_FORMS.FORM_SCHEMA_CREATE
 * @webhook
 * @eventType wix.forms.ai.v1.chat_settings_updated
 * @slug updated
 */
export declare function onChatSettingsUpdated(
  handler: (event: ChatSettingsUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a chat settings entity.
 *
 * The settings specified in a chat settings entity apply to the form with the same ID as the chat settings entity. Each form can have exactly one set of chat settings.
 * @param chatSettings - Chat settings to be created.
 * @public
 * @requiredField chatSettings
 * @requiredField chatSettings._id
 * @permissionId WIX_FORMS.FORM_SCHEMA_CREATE
 * @applicableIdentity APP
 * @returns Created chat settings.
 * @fqn wix.forms.ai.v1.ChatSettingsService.CreateChatSettings
 */
export async function createChatSettings(
  chatSettings: NonNullablePaths<ChatSettings, `_id`, 2>
): Promise<NonNullablePaths<ChatSettings, `_id` | `personality`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    chatSettings: chatSettings,
  });

  const reqOpts =
    ambassadorWixFormsAiV1ChatSettings.createChatSettings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.chatSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { chatSettings: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['chatSettings']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a chat settings entity.
 *
 * Changes take effect immediately for new interactive form sessions.
 * @param _id - The ID of the chat settings. The chat settings specified in this object apply to the form with a matching ID.
 * @public
 * @requiredField _id
 * @requiredField chatSettings
 * @permissionId WIX_FORMS.FORM_SCHEMA_UPDATE
 * @applicableIdentity APP
 * @returns Updated chat settings.
 * @fqn wix.forms.ai.v1.ChatSettingsService.UpdateChatSettings
 */
export async function updateChatSettings(
  _id: string,
  chatSettings: UpdateChatSettings
): Promise<NonNullablePaths<ChatSettings, `_id` | `personality`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    chatSettings: { ...chatSettings, id: _id },
  });

  const reqOpts =
    ambassadorWixFormsAiV1ChatSettings.updateChatSettings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.chatSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { chatSettings: '$[1]' },
        explicitPathsToArguments: { 'chatSettings.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'chatSettings']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateChatSettings {
  /**
   * The ID of the chat settings. The chat settings specified in this object apply to the form with a matching ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Custom greeting message displayed when a visitor opens the AI chat.
   * @maxLength 50
   */
  greetingMessage?: string | null;
  /**
   * Whether AI chat is enabled for this form.
   *
   * Default: `true`
   */
  chatEnabled?: boolean | null;
  /**
   * Revision number, which increments by 1 each time the chat settings are updated.
   * To prevent conflicting changes, the current revision must be passed when updating the chat settings.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the chat settings were created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the chat settings were last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Message that summarizes the contents of the form. Appears after the greeting message.
   * @maxLength 255
   */
  summaryMessage?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /**
   * Whether the summary message is manually set or AI-generated.
   * When `false` the summary message is automatically generated by AI when the chat settings are saved.
   * When `true`, the summary message can be manually set and won't be overwritten by AI.
   *
   * Default: `false`
   */
  manualSummary?: boolean | null;
  /**
   * Personality of the AI when interacting with visitors.
   * Defines the communication style of the AI chat responses.
   */
  personality?: PersonalityWithLiterals;
}

/**
 * Deletes a chat settings entity.
 * @param chatSettingsId - Chat settings ID.
 * @public
 * @requiredField chatSettingsId
 * @permissionId WIX_FORMS.FORM_SCHEMA_DELETE
 * @applicableIdentity APP
 * @fqn wix.forms.ai.v1.ChatSettingsService.DeleteChatSettings
 */
export async function deleteChatSettings(
  chatSettingsId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    chatSettingsId: chatSettingsId,
  });

  const reqOpts =
    ambassadorWixFormsAiV1ChatSettings.deleteChatSettings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { chatSettingsId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['chatSettingsId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a chat settings entity.
 * @param chatSettingsId - Chat settings ID.
 * @public
 * @requiredField chatSettingsId
 * @permissionId WIX_FORMS.FORM_SCHEMA_READ
 * @applicableIdentity APP
 * @returns Retrieved chat settings.
 * @fqn wix.forms.ai.v1.ChatSettingsService.GetChatSettings
 */
export async function getChatSettings(
  chatSettingsId: string
): Promise<NonNullablePaths<ChatSettings, `_id` | `personality`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    chatSettingsId: chatSettingsId,
  });

  const reqOpts = ambassadorWixFormsAiV1ChatSettings.getChatSettings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.chatSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { chatSettingsId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['chatSettingsId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of chat settings entities given the provided paging, filtering, and sorting.
 *
 * ### Defaults
 * Query Chat Settings has the following default settings, which you can override:
 * + Sorted by `id` in ascending order.
 * + `cursorPaging.limit` set to `100`.
 * @public
 * @permissionId WIX_FORMS.FORM_SCHEMA_READ
 * @applicableIdentity APP
 * @fqn wix.forms.ai.v1.ChatSettingsService.QueryChatSettings
 */
export function queryChatSettings(): ChatSettingsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    ChatSettings,
    'CURSOR',
    QueryChatSettingsRequest,
    QueryChatSettingsResponse
  >({
    func: async (payload: QueryChatSettingsRequest) => {
      const reqOpts =
        ambassadorWixFormsAiV1ChatSettings.queryChatSettings(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryChatSettingsRequest['query']) => {
      const args = [query, {}] as [QueryChatSettingsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryChatSettingsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.chatSettings,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface ChatSettingsQueryResult extends QueryCursorResult {
  items: ChatSettings[];
  query: ChatSettingsQueryBuilder;
  next: () => Promise<ChatSettingsQueryResult>;
  prev: () => Promise<ChatSettingsQueryResult>;
}

export interface ChatSettingsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName: 'greetingMessage' | 'summaryMessage',
    value: any
  ) => ChatSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName: 'greetingMessage' | 'summaryMessage',
    value: any
  ) => ChatSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: 'greetingMessage' | 'summaryMessage',
    value: any
  ) => ChatSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: 'greetingMessage' | 'summaryMessage',
    value: any
  ) => ChatSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: 'greetingMessage' | 'summaryMessage',
    value: any
  ) => ChatSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: 'greetingMessage' | 'summaryMessage',
    value: any
  ) => ChatSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: 'greetingMessage' | 'summaryMessage',
    value: string
  ) => ChatSettingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName: 'greetingMessage' | 'summaryMessage',
    value: any[]
  ) => ChatSettingsQueryBuilder;
  in: (
    propertyName: 'greetingMessage' | 'summaryMessage',
    value: any
  ) => ChatSettingsQueryBuilder;
  exists: (
    propertyName: 'greetingMessage' | 'summaryMessage',
    value: boolean
  ) => ChatSettingsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<'greetingMessage' | 'summaryMessage'>
  ) => ChatSettingsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<'greetingMessage' | 'summaryMessage'>
  ) => ChatSettingsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => ChatSettingsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => ChatSettingsQueryBuilder;
  find: () => Promise<ChatSettingsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.forms.ai.v1.ChatSettingsService.QueryChatSettings
 * @requiredField query
 */
export async function typedQueryChatSettings(
  query: ChatSettingsQuery
): Promise<
  NonNullablePaths<
    QueryChatSettingsResponse,
    | `chatSettings`
    | `chatSettings.${number}._id`
    | `chatSettings.${number}.personality`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixFormsAiV1ChatSettings.queryChatSettings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ChatSettingsQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['greetingMessage', 'summaryMessage'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  ChatSettings,
  ChatSettingsQuerySpec
>;
export type ChatSettingsQuery = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      ChatSettings,
      ChatSettingsQuerySpec,
      ChatSettingsQuery
    >(),
  },
};
